<template src="./PinCodeComponent.html"></template>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<script>
export default {
    props: {
        value: {
            type: String,
            required: true
        },
        length: {
            type: Number,
            default: 4
        },
        autofocus: {
            type: Boolean,
            default: true
        },
        secure: {
            type: Boolean,
            default: true
        },
        secureInputType: {
            type: String,
            default: 'password'
        },
        characterPreview: {
            type: Boolean,
            default: false
        },
        charPreviewDuration: {
            type: Number,
            default: 800
        }
    },
    emits: ['input'],
    data() {
        return {
            focusedCellIdx: 0,
            cells: [],
            watchers: {},
            cellsInputTypes: {},
            cellsHideChar: {}
        };
    },
    computed: {
        pinCodeComputed() {
            return this.cells.reduce((pin, cell) => pin + cell.value, '');
        }
    },
    watch: {
        value(value) {
            if (value) {
                this.onParentValueUpdated();
            } else {
                this.reset();
            }
        },
        length() {
            this.reset();
        },
        pinCodeComputed(val) {
            this.$emit('input', val);
        }
    },
    mounted() {
        this.init();
        // this.onParentValueUpdated(); // this.value used in function, but not set when mounted() is run
        if (this.autofocus) {
            this.$nextTick(this.focusCellByIndex);
        }
    },
    methods: {
        getRandomAutoCompleteValue() {
            const chars = 'abcdefghijklmnopqrstuvwxyz';
            let result = '';
            for (let i = 0; i < chars.length; i++) {
                result += chars.charAt(
                    Math.floor(Math.random() * chars.length)
                );
            }
            return result;
        },
        init() {
            const inputType = this.getRelevantInputType();
            for (let key = 0; key < this.length; key += 1) {
                this.setCellObject(key);
                this.setCellInputType(key, inputType);
                this.setCellWatcher(key);
            }
        },
        setCellObject(key) {
            this.cells[key] = { key, value: '' };
        },
        updateCellAfterPreview(index) {
            this.setCellInputType(index, this.secureInputType);
            this.setCellHideCharCssClass(index, true);
        },
        setCellInputType(index, inputType = 'password') {
            this.cellsInputTypes[index] = inputType;
        },
        setCellHideCharCssClass(index, hideChar) {
            this.cellsHideChar[index] = hideChar;
        },
        setCellWatcher(index) {
            const watchingProperty = `cells.${index}.value`;
            this.watchers[watchingProperty] = this.$watch(
                watchingProperty,
                (newVal, oldVal) => this.onCellChanged(index, newVal, oldVal)
            );
        },
        onParentValueUpdated() {
            if (this.value.length !== this.length) {
                this.$emit('input', this.pinCodeComputed);
                return;
            }
            this.value.split('').forEach((cell, idx) => {
                this.cells[idx].value = cell || '';
            });
        },
        onCellChanged(index, newVal /*, oldVal*/) {
            if (!this.isTheCellValid(newVal, false)) {
                this.cells[index].value = '';
                return;
            }
            this.focusNextCell();
            /* performing character preview if it's enabled */
            if (this.secure && this.characterPreview) {
                this.setCellHideCharCssClass(index, false);
                setTimeout(
                    this.updateCellAfterPreview,
                    this.charPreviewDuration,
                    index
                );
            }
        },
        onCellErase(index, event) {
            const isThisCellFilled = this.cells[index].value.length;
            if (!isThisCellFilled) {
                this.focusPreviousCell();
                event.preventDefault();
            }
        },
        onKeyDown(event) {
            switch (event.keyCode) {
                /* left arrow key */
                case 37:
                    this.focusPreviousCell();
                    break;
                /* right arrow key */
                case 39:
                    this.focusNextCell();
                    break;
                /* delete key */
                case 46:
                    this.reset();
                    break;
                default:
                    break;
            }
        },
        reset() {
            this.unwatchCells();
            this.init();
            this.focusCellByIndex();
        },
        unwatchCells() {
            const watchers = Object.keys(this.watchers);
            watchers.forEach((name) => this.watchers[name]());
        },
        isTheCellValid(cell, allowEmpty = true) {
            if (!cell) {
                return allowEmpty ? cell === '' : false;
            }
            return !!cell.match('^\\d{1}$');
        },
        getRelevantInputType() {
            return this.secure && !this.characterPreview
                ? this.secureInputType
                : 'tel';
        },
        focusPreviousCell() {
            if (!this.focusedCellIdx) {
                return;
            }
            this.focusCellByIndex(this.focusedCellIdx - 1);
        },
        focusNextCell() {
            if (this.focusedCellIdx === this.length - 1) {
                return;
            }
            this.focusCellByIndex(this.focusedCellIdx + 1);
        },
        focusCellByIndex(index = 0) {
            const ref = `input${index}`;
            const refWrapper = this.$refs[ref];
            // For some reason refWrapper has been a single input instance but sometimes an array of inputs
            const el = Array.isArray(refWrapper) ? refWrapper[0] : refWrapper;
            if (el) {
                el.focus();
                el.select();
            }
            this.focusedCellIdx = index;
        }
    }
};
</script>

<style scoped lang="scss" src="./PinCodeComponent.scss"></style>
